/* eslint-disable react/no-unstable-nested-components */
import { SplashScreen, Tabs, usePathname, useRouter } from 'expo-router';
import { useCallback, useEffect } from 'react';
import { Platform, TouchableOpacity } from 'react-native';
import { UnistylesRuntime, useStyles } from 'react-native-unistyles';

import { useModalStore } from '@/components/login-modal/store';
import { isDesktop, useAuth, useIsFirstTime } from '@/core';
import useSearchStore from '@/stores/use-search-store';
import useSettingsStore from '@/stores/use-settings-store';
import { GREY_40 } from '@/style/colors';
import { Icon } from '@/ui/icon';
import { Person, Search } from '@/ui/icons';
import { Calendar as CalendarIcon } from '@/ui/icons/calendar';
import { EonSubscription } from '@/ui/icons/eon-subscription';

export default function TabLayout() {
  const { theme } = useStyles();
  const router = useRouter();
  const status = useAuth.use.status();
  const [isFirstTime] = useIsFirstTime();
  const pathname = usePathname();
  const hideSplash = useCallback(async () => {
    await SplashScreen.hideAsync();
  }, []);
  const viewMode = useSearchStore((state) => state.viewMode);
  const setLoginModalMode = useModalStore((state) => state.setLoginModalMode);
  const loginModalMode = useModalStore((state) => state.loginModalMode);
  const partnerMode = useSettingsStore((state) => state.partnerMode);

  useEffect(() => {
    if (Platform.OS !== 'web' && partnerMode && status === 'signIn') {
      router.replace('/partner/dashboard');
    }
  }, [partnerMode, status]);

  useEffect(() => {
    if (status !== 'idle') {
      setTimeout(() => {
        hideSplash();
      }, 1000);
    }
  }, [hideSplash, status]);

  useEffect(() => {
    if (
      status === 'signOut' &&
      loginModalMode === 'none' &&
      ![
        '/',
        '/search-details',
        '/search',
        '/account/support-faq',
        '/subscriptions',
        '/subscriptions/onboarding',
      ].includes(pathname) &&
      !['/car', '/contract-sign', '/login', '/confirm-new-password'].some(
        (substring) => pathname.includes(substring)
      )
    ) {
      // Defer to the next tick so the root navigator has finished mounting,
      // otherwise a cold deep-link load throws "Attempted to navigate before
      // mounting the Root Layout component."
      const timer = setTimeout(() => {
        router.replace(`/login?returnTo=${encodeURIComponent(pathname)}`);
      }, 0);

      return () => clearTimeout(timer);
    }
  }, [status, pathname, loginModalMode, router]);

  useEffect(() => {
    if (
      status === 'signOut' &&
      [
        '/',
        '/account/support-faq',
        '/subscriptions',
        '/subscriptions/onboarding',
      ].includes(pathname)
    ) {
      setLoginModalMode('none');
    }
  }, [status, pathname, setLoginModalMode]);

  // if (isFirstTime) {
  //   return <Redirect href="/home" />;
  // }

  return (
    <Tabs
      screenOptions={({ route }) => ({
        sceneStyle: {
          backgroundColor: theme.colors.background_1,
        },
        tabBarLabelStyle: {
          fontFamily: 'DMSans-Regular',
          includeFontPadding: false,
        },
        tabBarStyle: {
          backgroundColor: theme.colors.background_2,
          borderTopColor: theme.colors.card.default.borderColor,
          borderColor: theme.colors.card.default.borderColor,
          borderTopWidth: 1,
          height: 60 + UnistylesRuntime.insets.bottom,
          paddingBottom: UnistylesRuntime.insets.bottom,
          ...(pathname === '/' &&
            viewMode === 'map' && {
              height: 0,
              display: 'none',
            }),
          ...(isDesktop() && {
            height: 0,
            display: 'none',
          }),
          ...(pathname.includes('/contract-sign') && {
            height: 0,
            display: 'none',
          }),
        },
        headerStyle: {
          backgroundColor: theme.colors.background_1,
        },
        headerTintColor: theme.colors.text.primary,
        headerShadowVisible: false,
        tabBarActiveTintColor: theme.colors.text.primary,
        tabBarInactiveTintColor: GREY_40,
        tabBarIcon: ({ focused }) => {
          if (route.name === 'search') {
            return (
              <Icon
                type="naked"
                icon={<Search />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'trips') {
            return (
              <Icon
                type="naked"
                icon={<CalendarIcon />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'subscriptions') {
            return (
              <Icon
                type="naked"
                icon={<EonSubscription />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          } else if (route.name === 'account') {
            return (
              <Icon
                type="naked"
                icon={<Person />}
                width={22}
                height={22}
                fillColor={focused ? theme.colors.icon.nakedColor : GREY_40}
              />
            );
          }
        },
      })}
    >
      <Tabs.Screen
        name="search"
        options={{
          title: 'Search',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/search');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="trips"
        options={{
          title: 'Trips',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/trips');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="subscriptions"
        options={{
          title: 'Subscription',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/subscriptions');
                }}
              />
            );
          },
        }}
      />

      <Tabs.Screen
        name="account"
        options={{
          title: 'Account',
          headerShown: false,
          tabBarButton: (props) => {
            return (
              <TouchableOpacity
                {...props}
                onPress={(e) => {
                  e.preventDefault(); // Prevent the default tab behavior
                  if (Platform.OS === 'android' && router.canDismiss()) {
                    router.dismissAll();
                  }
                  router.navigate('/account');
                }}
              />
            );
          },
        }}
      />
    </Tabs>
  );
}
